import { Field, SaveBar, useDraft } from "@/components/hq/useDraft";
import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ImageField } from "@/components/hq/ImageField";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Trash2 } from "lucide-react";

const inputCls = "rounded-sm bg-[#0A0A0A]";
const monoCls = "mono rounded-sm bg-[#0A0A0A] text-sm";

const PhotoField = ({ photo, onChange }) => (
  <ImageField
    label="PHOTO"
    testId={`hq-cred-photo-${photo.id}`}
    value={photo.url || ""}
    onChange={(url) => onChange({ url })}
    placeholder="Paste a URL or upload a real event photo"
  />
);

export const CredibilityEditor = () => {
  const { draft, update, save, dirty } = useDraft("credibility");
  if (!draft) return null;

  const setPhoto = (id, patch) =>
    update({
      ...draft,
      photos: (draft.photos || []).map((p) => (p.id === id ? { ...p, ...patch } : p)),
    });
  const setMilestone = (id, patch) =>
    update({
      ...draft,
      milestones: (draft.milestones || []).map((m) => (m.id === id ? { ...m, ...patch } : m)),
    });

  return (
    <div data-testid="hq-credibility">
      <SaveBar dirty={dirty} save={save} testId="credibility" />

      <div className="space-y-4">
        <Field label="EYEBROW">
          <Input value={draft.eyebrow || ""} onChange={(e) => update({ ...draft, eyebrow: e.target.value })} className={inputCls} />
        </Field>
        <Field label="SECTION TITLE">
          <Input
            data-testid="hq-cred-title"
            value={draft.title || ""}
            onChange={(e) => update({ ...draft, title: e.target.value })}
            className={`${inputCls} display text-xl`}
          />
        </Field>
        <Field label="INTRO">
          <Textarea value={draft.intro || ""} onChange={(e) => update({ ...draft, intro: e.target.value })} className={inputCls} rows={3} />
        </Field>
        <Field label="AMBIENT CLIP PATH (MUTED, LOOPING — LEAVE BLANK TO HIDE)">
          <Input
            data-testid="hq-cred-clip"
            value={draft.clip || ""}
            onChange={(e) => update({ ...draft, clip: e.target.value })}
            placeholder="/brand/drink-build-loop.mp4"
            className={monoCls}
          />
        </Field>
        <Field label="CLIP CAPTION">
          <Input value={draft.clip_caption || ""} onChange={(e) => update({ ...draft, clip_caption: e.target.value })} className={inputCls} />
        </Field>
      </div>

      <div className="mt-10">
        <div className="flex items-center justify-between">
          <h3 className="display text-2xl uppercase">Real event photos</h3>
          <button
            data-testid="hq-cred-photo-add"
            onClick={() =>
              update({
                ...draft,
                photos: [...(draft.photos || []), { id: `ph-${Date.now()}`, url: "", caption: "" }],
              })
            }
            className="mono text-[11px] tracking-widest text-primary hover:underline"
          >
            + ADD PHOTO
          </button>
        </div>
        <p className="mono mt-1 text-[10px] leading-relaxed text-muted-foreground">
          THE FIRST PHOTO RENDERS LARGE ON THE HOMEPAGE. EMPTY SLOTS ARE NOT SHOWN TO VISITORS.
        </p>
        <div className="mt-4 space-y-4">
          {(draft.photos || []).map((p) => (
            <div key={p.id} className="border border-border bg-[#141414] rounded-sm p-4" data-testid={`hq-cred-photo-${p.id}`}>
              <div className="flex items-center justify-between">
                <span className="mono text-[10px] text-muted-foreground">{p.id}</span>
                <button
                  onClick={() => update({ ...draft, photos: draft.photos.filter((x) => x.id !== p.id) })}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 size={15} />
                </button>
              </div>
              <PhotoField photo={p} onChange={(patch) => setPhoto(p.id, patch)} />
              <Field label="CAPTION (WHAT EVENT, WHERE)">
                <Input
                  data-testid={`hq-cred-caption-${p.id}`}
                  value={p.caption || ""}
                  onChange={(e) => setPhoto(p.id, { caption: e.target.value })}
                  placeholder="Provo Farmers Market — full setup, Saturday morning"
                  className={inputCls}
                />
              </Field>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10">
        <div className="flex items-center justify-between">
          <h3 className="display text-2xl uppercase">Verified milestones</h3>
          <button
            data-testid="hq-cred-milestone-add"
            onClick={() =>
              update({
                ...draft,
                milestones: [
                  ...(draft.milestones || []),
                  { id: `ms-${Date.now()}`, label: "", detail: "" },
                ],
              })
            }
            className="mono text-[11px] tracking-widest text-primary hover:underline"
          >
            + ADD MILESTONE
          </button>
        </div>
        <p className="mono mt-1 text-[10px] leading-relaxed text-muted-foreground">
          ONLY ADD FACTS YOU CAN VERIFY. NO REVENUE, ATTENDANCE OR SALES CLAIMS UNLESS THEY ARE
          REAL AND YOURS. EMPTY SLOTS ARE NOT SHOWN.
        </p>
        <div className="mt-4 space-y-3">
          {(draft.milestones || []).map((m) => (
            <div key={m.id} className="border border-border bg-[#141414] rounded-sm p-4" data-testid={`hq-cred-milestone-${m.id}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1 space-y-3">
                  <Input
                    data-testid={`hq-cred-milestone-label-${m.id}`}
                    value={m.label || ""}
                    onChange={(e) => setMilestone(m.id, { label: e.target.value })}
                    placeholder="Milestone (e.g. 3 seasons of live events)"
                    className={`${inputCls} display text-xl`}
                  />
                  <Input
                    value={m.detail || ""}
                    onChange={(e) => setMilestone(m.id, { detail: e.target.value })}
                    placeholder="Short supporting detail"
                    className={inputCls}
                  />
                </div>
                <button
                  onClick={() =>
                    update({ ...draft, milestones: draft.milestones.filter((x) => x.id !== m.id) })
                  }
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const NurtureEditor = () => {
  const { draft, update, save, dirty } = useDraft("nurture");
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  if (!draft) return null;

  const setStep = (id, patch) =>
    update({ ...draft, steps: draft.steps.map((s) => (s.id === id ? { ...s, ...patch } : s)) });

  return (
    <div data-testid="hq-nurture">
      <SaveBar
        dirty={dirty}
        save={save}
        testId="nurture"
        addLabel="+ Day"
        onAdd={() =>
          update({
            ...draft,
            steps: [
              ...draft.steps,
              {
                id: `d${Date.now()}`,
                day: (draft.steps.at(-1)?.day ?? -1) + 1,
                subject: "",
                body: "",
              },
            ],
          })
        }
      />

      <div
        data-testid="hq-nurture-status"
        className={`flex flex-wrap items-center justify-between gap-4 border px-5 py-4 ${
          draft.enabled ? "border-primary/60 bg-[#191603]" : "border-border bg-[#141414]"
        }`}
      >
        <div>
          <div className="mono text-[10px] tracking-[0.3em] text-primary">
            SENDING {draft.enabled ? "LIVE" : "OFF"}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {draft.enabled
              ? "Live. Every enrolled lead gets the next due email once a day, in order, and never the same day twice."
              : "Leads are enrolled and their day position is tracked, but nothing sends while this is off."}
          </p>
        </div>
        <div className="flex w-full shrink-0 flex-wrap items-center gap-3 md:w-auto">
          <Input
            data-testid="hq-nurture-test-email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@email.com"
            className={`${monoCls} w-full md:w-56`}
          />
          <button
            data-testid="hq-nurture-test-send"
            disabled={sending || !testTo}
            onClick={async () => {
              setSending(true);
              try {
                await api.post("/nurture/test", { to: testTo, day: 0 });
                toast.success(`Day 0 test sent to ${testTo}`);
              } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
              } finally {
                setSending(false);
              }
            }}
            className="mono rounded-sm border border-border px-4 py-2.5 text-[10px] tracking-widest text-primary transition-colors hover:border-primary disabled:opacity-40"
          >
            {sending ? "SENDING…" : "SEND ME DAY 0"}
          </button>
          <button
            data-testid="hq-nurture-toggle"
            onClick={() => update({ ...draft, enabled: !draft.enabled })}
            className="display rounded-sm border border-primary px-5 py-2.5 text-lg uppercase text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
          >
            {draft.enabled ? "Switch sending off" : "Turn sending on"}
          </button>
        </div>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <Field label="FROM NAME">
          <Input value={draft.from_name || ""} onChange={(e) => update({ ...draft, from_name: e.target.value })} className={inputCls} />
        </Field>
        <Field label="REPLY-TO EMAIL">
          <Input value={draft.reply_to || ""} onChange={(e) => update({ ...draft, reply_to: e.target.value })} placeholder="hello@smashlemonade.com" className={inputCls} />
        </Field>
      </div>

      <div className="mt-8">
        <button
          data-testid="hq-nurture-preview-toggle"
          onClick={async () => {
            if (preview) return setPreview(null);
            try {
              const { data } = await api.get("/nurture/preview");
              setPreview(data);
            } catch (e) {
              toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            }
          }}
          className="mono border border-border px-4 py-2.5 text-[10px] tracking-widest text-primary transition-colors hover:border-primary"
        >
          {preview ? "BACK TO EDITING" : "PREVIEW AS A LEAD"}
        </button>
        {dirty && !preview ? (
          <span className="mono ml-3 text-[10px] tracking-widest text-muted-foreground">
            SAVE FIRST — THE PREVIEW SHOWS WHAT IS SAVED
          </span>
        ) : null}
      </div>

      {preview ? (
        <div data-testid="hq-nurture-preview" className="mt-6 space-y-6">
          <p className="mono text-[10px] leading-relaxed tracking-widest text-muted-foreground">
            ALL {preview.emails.length} EMAILS AS A LEAD NAMED ALEX RECEIVES THEM — FROM{" "}
            {(preview.from_name || "").toUpperCase()}, REPLIES GO TO{" "}
            {(preview.reply_to || "").toUpperCase()}.
          </p>
          {preview.emails.map((e) => (
            <div key={e.day} data-testid={`hq-nurture-preview-day-${e.day}`} className="border border-border">
              <div className="flex flex-wrap items-center gap-3 border-b border-border bg-[#141414] px-5 py-3">
                <span className="mono shrink-0 bg-primary px-2 py-1 text-[10px] tracking-widest text-primary-foreground">
                  DAY {e.day}
                </span>
                <span className="text-sm text-white">{e.subject}</span>
              </div>
              <iframe
                title={`Day ${e.day} email`}
                srcDoc={`<body style="margin:0;background:#fff">${e.html}</body>`}
                className="h-[420px] w-full bg-white"
              />
            </div>
          ))}
        </div>
      ) : (
      <div className="mt-8 space-y-4">
        {draft.steps.map((s) => (
          <div key={s.id} className="border border-border bg-[#141414] rounded-sm p-5" data-testid={`hq-nurture-step-${s.day}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span className="display shrink-0 text-3xl text-primary">DAY {s.day}</span>
              <Input
                type="number"
                value={s.day}
                onChange={(e) => setStep(s.id, { day: Number(e.target.value) })}
                className={`${monoCls} w-20`}
              />
              <Input
                data-testid={`hq-nurture-subject-${s.day}`}
                value={s.subject || ""}
                onChange={(e) => setStep(s.id, { subject: e.target.value })}
                placeholder="Subject line"
                className={`${inputCls} flex-1 md:min-w-[20rem]`}
              />
              <button
                onClick={() => update({ ...draft, steps: draft.steps.filter((x) => x.id !== s.id) })}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 size={16} />
              </button>
            </div>
            <div className="mt-3">
              <Field label="EMAIL BODY">
                <Textarea
                  data-testid={`hq-nurture-body-${s.day}`}
                  value={s.body || ""}
                  onChange={(e) => setStep(s.id, { body: e.target.value })}
                  className={inputCls}
                  rows={6}
                />
              </Field>
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
};
